import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { DialogModule } from "primeng/dialog";
import { TableModule } from "primeng/table";
import { ButtonModule } from "primeng/button";
import { PaginatorModule, PaginatorState } from "primeng/paginator";
import { CheckboxModule } from "primeng/checkbox";
import { GenericRequestDto } from "../../../../../core/models/generic-request.dto";
import { GetAllSamplepo } from "../../../../../core/models/samplePO/details/getAllSamplepo";
import { PurchaseOrderLineSummary } from "../../../../../core/models/samplePO/details/samplePOSummaryDto";
import { WorkflowSampleLineItemDto } from "../../../../../core/models/project-workflow/workflowSampleLineDto";
import { SamplepoListingService } from "../../../../../core/services/samplepo-listing.service";
import { SpinnerComponent } from "../../../../../shared/components/spinner/spinner.component";

@Component({
  selector: "jni-add-sample-po-modal",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    ButtonModule,
    PaginatorModule,
    CheckboxModule,
    SpinnerComponent,
  ],
  templateUrl: "./add-sample-po-modal.component.html",
  styleUrls: ["./add-sample-po-modal.component.scss"],
})
export class AddSamplePoModalComponent {
  private readonly _samplepoListingService = inject(SamplepoListingService);

  visible = input(false);
  /** Client name of the current workflow — sample POs are filtered to this client. */
  clientName = input("");

  visibleChange = output<boolean>();
  loadingChange = output<boolean>();
  /** Emits the selected sample PO order lines when the user clicks Save. */
  linesSelected = output<WorkflowSampleLineItemDto[]>();

  samplePOs = signal<GetAllSamplepo[]>([]);
  lineItems = signal<PurchaseOrderLineSummary[]>([]);
  loadingSamplePOs = signal(false);
  loadingLineItems = signal(false);
  totalRecords = signal(0);
  /** The dialog is only revealed once the initial data has arrived. */
  dialogVisible = signal(false);

  selectedSamplePOs: GetAllSamplepo[] = [];
  selectedLineItems: PurchaseOrderLineSummary[] = [];

  rows = 10;
  first = 0;

  /** Fetched order lines cached per sample PO number. */
  private linesByPo = new Map<string, PurchaseOrderLineSummary[]>();

  constructor() {
    effect(() => {
      if (this.visible()) {
        this.resetState();
        this.openWhenDataReady();
      } else {
        this.dialogVisible.set(false);
      }
    });
  }

  onPageChange(event: PaginatorState): void {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 10;
    this.loadSamplePOs();
  }

  /** Fetches the first page behind the global loader, then reveals the dialog. */
  private openWhenDataReady(): void {
    this.loadingChange.emit(true);
    this._samplepoListingService
      .getAllSamplePOPaginated(this.buildRequest())
      .subscribe({
        next: res => {
          this.samplePOs.set(res.data ?? []);
          this.totalRecords.set(res.totalRecords ?? 0);
          this.loadingChange.emit(false);
          this.dialogVisible.set(true);
        },
        error: () => {
          this.loadingChange.emit(false);
          this.visibleChange.emit(false);
        },
      });
  }

  private buildRequest(): GenericRequestDto {
    return {
      skip: this.first,
      take: this.rows,
      filters: [
        {
          columnName: "NameAliasD365",
          tableName: "Client",
          matchMode: "contains",
          value: this.clientName(),
        },
      ],
      sort: [],
    };
  }

  /** Loads a page of sample POs filtered by the workflow's client. */
  private loadSamplePOs(): void {
    this.loadingSamplePOs.set(true);
    this._samplepoListingService
      .getAllSamplePOPaginated(this.buildRequest())
      .subscribe({
        next: res => {
          this.samplePOs.set(res.data ?? []);
          this.totalRecords.set(res.totalRecords ?? 0);
          this.loadingSamplePOs.set(false);
        },
        error: () => this.loadingSamplePOs.set(false),
      });
  }

  /** Syncs the order-lines grid with the currently selected sample POs. */
  onSamplePoSelectionChange(): void {
    const selectedPoNumbers = new Set(
      this.selectedSamplePOs
        .map(po => po.purchaseOrderNumber)
        .filter((po): po is string => !!po)
    );

    // Drop cached lines and selections of deselected POs
    for (const poNumber of [...this.linesByPo.keys()]) {
      if (!selectedPoNumbers.has(poNumber)) {
        this.linesByPo.delete(poNumber);
      }
    }
    this.selectedLineItems = this.selectedLineItems.filter(line =>
      selectedPoNumbers.has(line.purchaseOrderNumberD365 ?? "")
    );

    const poNumbersToLoad = [...selectedPoNumbers].filter(
      poNumber => !this.linesByPo.has(poNumber)
    );
    if (poNumbersToLoad.length === 0) {
      this.rebuildLineItems();
      return;
    }

    this.loadingLineItems.set(true);
    let pending = poNumbersToLoad.length;
    for (const poNumber of poNumbersToLoad) {
      this._samplepoListingService
        .getAllD365InventoryLines(poNumber)
        .subscribe({
          next: res => {
            this.linesByPo.set(poNumber, res.data ?? []);
            pending--;
            if (pending === 0) {
              this.loadingLineItems.set(false);
              this.rebuildLineItems();
            }
          },
          error: () => {
            this.linesByPo.set(poNumber, []);
            pending--;
            if (pending === 0) {
              this.loadingLineItems.set(false);
              this.rebuildLineItems();
            }
          },
        });
    }
  }

  private rebuildLineItems(): void {
    // Order line items by sample PO number regardless of the order the POs were checked in.
    const orderedPos = [...this.selectedSamplePOs].sort((a, b) =>
      (a.purchaseOrderNumber ?? "").localeCompare(
        b.purchaseOrderNumber ?? "",
        undefined,
        { numeric: true }
      )
    );
    const lines: PurchaseOrderLineSummary[] = [];
    for (const po of orderedPos) {
      if (po.purchaseOrderNumber) {
        lines.push(...(this.linesByPo.get(po.purchaseOrderNumber) ?? []));
      }
    }
    this.lineItems.set(lines);
  }

  private resetState(): void {
    this.first = 0;
    this.samplePOs.set([]);
    this.lineItems.set([]);
    this.loadingLineItems.set(false);
    this.selectedSamplePOs = [];
    this.selectedLineItems = [];
    this.linesByPo.clear();
  }

  closeModal(): void {
    this.visibleChange.emit(false);
  }

  save(): void {
    const lines: WorkflowSampleLineItemDto[] = this.selectedLineItems.map(
      line => ({
        purchaseOrderLineId: line.id,
        purchaseOrderNumberD365: line.purchaseOrderNumberD365 ?? "",
        lineRecIdD365: line.lineRecIdD365,
      })
    );
    this.linesSelected.emit(lines);
  }
}
